// Lib
import React, { useMemo } from 'react';

// Components
import SelectedElementsDragPreviews from '../../../element/dnd/SelectedElementsDragPreviews';
import ElementDragGroupPositioner from '../../../element/dnd/ElementDragGroupPositioner';
import ElementDragPreviewRotator from './ElementDragPreviewRotator';
import ElementDragLayerDebugUI from './ElementDragLayerDebugUI';

// Util
import { getElementDragOrigin } from '../../../utils/dnd/dragPositionUtils';

// Types
import { Point } from '../../../../common/maths/geometry/pointTypes';
import { ImMap, ImmutableMap } from '../../../../common/utils/immutableHelper';
import { DNDDebugToolProperties } from '../../../debug/dragAndDrop/DragAndDropDebugToolTypes';

// Constants
import { ELEMENT_DND_TYPE } from '../../../../common/elements/elementConstants';

type ElementDragLayerDisplayProps = {
    // props passed from parent
    currentBoardId: string;
    permissions: number;

    // from redux
    gridSize: number;
    zoomScale: number;
    isReplaceModeActive: string;
    dragModifierKeys: ImmutableMap;
    areDnd3dEffectsEnabled: boolean;
    attachModeHoveredId: string;

    // from drag layer
    item: any;
    itemType: string;
    initialClientOffset: Point;
    initialSourceClientOffset: Point;

    // from elementDragLayerStateManager
    groupPosition: Point;
    elementRotation: Point;
    shadowIntensity: number;
    zoomScaleOnDragSource: number;
    movementOffset: Point;
    clientOffset: Point;
    sourceClientOffset: Point;

    // from memoised subsection of drag layer
    hoveredTypes: Array<string>;
    zoomScaleOnHoveredTarget: number;

    // debugging
    dndDebugToolProperties: ImMap<DNDDebugToolProperties>;
};

const ElementDragLayerDisplay = ({
    item,
    itemType,
    initialClientOffset,
    initialSourceClientOffset,

    hoveredTypes,
    isReplaceModeActive,
    dragModifierKeys,
    gridSize,
    currentBoardId,
    groupPosition,
    elementRotation,
    shadowIntensity,
    clientOffset,
    sourceClientOffset,
    permissions,
    zoomScaleOnHoveredTarget,
    zoomScaleOnDragSource,
    dndDebugToolProperties,
}: ElementDragLayerDisplayProps) => {
    const dragOrigin = useMemo(() => {
        if (itemType !== ELEMENT_DND_TYPE) return null;
        if (!initialClientOffset) return null;

        return getElementDragOrigin({
            item,
            gridSize,
            initialClientOffset,
            initialSourceClientOffset,
        });
    }, [itemType, initialClientOffset]);

    const previews = useMemo(
        () => (
            <SelectedElementsDragPreviews
                dragModifierKeys={dragModifierKeys}
                item={item}
                gridSize={gridSize}
                currentBoardId={currentBoardId}
                permissions={permissions}
                zoomScaleOnDragSource={zoomScaleOnDragSource}
                dndDebugToolProperties={dndDebugToolProperties}
            />
        ),
        [dragModifierKeys, item, gridSize, currentBoardId, permissions, dndDebugToolProperties],
    );

    if (itemType !== ELEMENT_DND_TYPE || !item || !item.element || item.disableDragPreview) return null;

    return (
        <div className="ElementDragLayer">
            <ElementDragLayerDebugUI
                item={item}
                clientOffset={clientOffset}
                sourceClientOffset={sourceClientOffset}
                dndDebugToolProperties={dndDebugToolProperties}
            />

            <ElementDragPreviewRotator elementRotation={elementRotation} shadowIntensity={shadowIntensity}>
                <ElementDragGroupPositioner
                    zoomScaleOnHoveredTarget={zoomScaleOnHoveredTarget}
                    zoomScaleOnDragSource={zoomScaleOnDragSource}
                    groupPosition={groupPosition}
                    hoveredTypes={hoveredTypes}
                    isReplaceModeActive={isReplaceModeActive}
                    dragOrigin={dragOrigin}
                    dndDebugToolProperties={dndDebugToolProperties}
                >
                    {previews}
                </ElementDragGroupPositioner>
            </ElementDragPreviewRotator>
        </div>
    );
};

export default ElementDragLayerDisplay;
